import { SignIn, useAuth } from "@mattfinkel/common-web";
import type { ReactNode } from "react";
import { Navigate, Route, Routes } from "react-router";
import { CaptureProvider } from "./components/CaptureProvider";
import { Layout } from "./components/Layout";
import { auth, googleProvider } from "./firebase";
import BiltCash from "./pages/BiltCash";
import Detail from "./pages/Detail";
import Cards from "./pages/Cards";
import Credits from "./pages/Credits";
import Flights from "./pages/Flights";
import NotFound from "./pages/NotFound";
import Offers from "./pages/Offers";
import Points from "./pages/Points";
import Refresh from "./pages/Refresh";
import Settings from "./pages/Settings";

/** The centred card the app shows before it has an authorized session. */
function Gate({ children }: { children: ReactNode }) {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-8">
      <div className="w-full max-w-md space-y-6">
        <header>
          <h1 className="wordmark text-3xl">
            Wallet<span className="text-accent">.</span>
          </h1>
          <p className="mt-2 text-sm text-neutral-400">Credit card benefits and loyalty points</p>
        </header>
        {children}
      </div>
    </div>
  );
}

export default function App() {
  const { state, signOut, retry } = useAuth();

  // Routes are mounted ONLY inside the allowed branch, so no page component
  // can render for an unauthorized session even if someone deep-links to it.
  // The backend enforces the same allowlist on every request regardless -- the
  // UI gate is not the boundary -- but this keeps the two consistent.
  if (state.status === "allowed") {
    return (
      // Above the router: one capture port for the whole session, so a run's
      // pushed progress survives navigating away from /refresh and back.
      <CaptureProvider>
        <Routes>
          <Route element={<Layout email={state.user.email} onSignOut={signOut} />}>
            <Route index element={<Credits />} />
            {/* Merged into Cards (2026-09-10); an old link or bookmark lands
                there rather than on Not found. */}
            <Route path="which-card" element={<Navigate to="/cards" replace />} />
            <Route path="cards" element={<Cards />} />
            <Route path="offers" element={<Offers />} />
            <Route path="points" element={<Points />} />
            {/* Not in NAV: the header control is the only way in, and it is drawn
                only once the extension has answered. Reached without a bridge,
                the page redirects to Points rather than showing dead controls. */}
            <Route path="refresh" element={<Refresh />} />
            <Route path="bilt-cash" element={<BiltCash />} />
            <Route path="travel" element={<Flights />} />
            <Route path="settings" element={<Settings />} />
            <Route path="detail/:id" element={<Detail />} />
            {/* Hosting's SPA catch-all serves index.html for any unknown path,
                so an unmatched deep link arrives here rather than as a CDN 404. */}
            <Route path="*" element={<NotFound />} />
          </Route>
        </Routes>
      </CaptureProvider>
    );
  }

  return (
    <Gate>
      {state.status === "idle" && <SignIn auth={auth} provider={googleProvider} />}

      {state.status === "verifying" && (
        <div className="rounded-md border border-neutral-800 bg-neutral-900 p-4 text-sm">
          Verifying access…
        </div>
      )}

      {/* "Could not ask" is NOT "denied", and collapsing the two is the bug
          this state exists to prevent: a scale-to-zero cold start or a
          transient 5xx would otherwise sign a legitimate user out and tell
          them they are not allowed. They keep their session and get a retry. */}
      {state.status === "unavailable" && (
        <div className="space-y-3">
          <div className="rounded-md border border-amber-900 bg-amber-950/40 p-4 text-sm">
            Could not reach the server: {state.reason}
          </div>
          <button
            onClick={retry}
            className="btn"
          >
            Try again
          </button>
        </div>
      )}

      {state.status === "denied" && (
        <div className="space-y-3">
          <div className="rounded-md border border-red-900 bg-red-950/40 p-4 text-sm">
            Access denied: {state.reason}
          </div>
          <SignIn auth={auth} provider={googleProvider} />
        </div>
      )}
    </Gate>
  );
}
